<script setup>
// NOTICE: KAPTION-INTEGRATION - whatsapp-flows: the composer "Flow" picker.
// Lists all the inbox's flows: published/throttled ones are selectable,
// drafts/deprecated/blocked are shown disabled with the reason (same reasons
// as the template builder's flow selector). Sends the picked one as an
// interactive flow message (POST whatsapp_flow_messages). Interactive flows only work inside
// the 24h window, so outside it the button is disabled and says why.
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import KaptionWhatsappFlowsAPI from 'dashboard/api/kaptionWhatsappFlows';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
import {
  WHATSAPP_FLOWS_FLAG,
  flowTemplateUnavailableReason,
} from 'dashboard/helper/kaptionWhatsappFlows';

const props = defineProps({
  conversationId: { type: [Number, String], default: null },
  inbox: { type: Object, default: () => ({}) },
  canReply: { type: Boolean, default: false },
  isNote: { type: Boolean, default: false },
});

const { t } = useI18n();
const { isKaptionFlagEnabled } = useKaptionFeatureFlags();

const isOpen = ref(false);
const isLoading = ref(false);
const isSending = ref(false);
const flows = ref([]);
const selected = ref(null);
const cta = ref('');
const body = ref('');

const isVisible = computed(
  () =>
    isKaptionFlagEnabled(WHATSAPP_FLOWS_FLAG) &&
    !props.isNote &&
    !!props.conversationId &&
    props.inbox?.channel_type === 'Channel::Whatsapp' &&
    props.inbox?.provider === 'whatsapp_cloud'
);

// Sendable flows first, then the rest (each group keeps the API's order).
const flowOptions = computed(() => {
  const options = flows.value.map(flow => {
    const reason = flowTemplateUnavailableReason(flow.status);
    return {
      flow,
      disabled: !!reason,
      reason: reason
        ? t(`WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.FLOW_REASON_${reason}`)
        : '',
    };
  });
  return [
    ...options.filter(o => !o.disabled),
    ...options.filter(o => o.disabled),
  ];
});

const hasSendable = computed(() => flowOptions.value.some(o => !o.disabled));

const emptyMessage = computed(() => {
  if (!flows.value.length) return t('KAPTION_WHATSAPP_FLOWS.COMPOSER.NONE');
  if (!hasSendable.value)
    return t('KAPTION_WHATSAPP_FLOWS.COMPOSER.NONE_SENDABLE');
  return '';
});

const tooltip = computed(() =>
  props.canReply
    ? t('KAPTION_WHATSAPP_FLOWS.COMPOSER.TITLE')
    : t('KAPTION_WHATSAPP_FLOWS.COMPOSER.WINDOW_CLOSED')
);

const loadFlows = async () => {
  isLoading.value = true;
  try {
    const { data } = await KaptionWhatsappFlowsAPI.list(props.inbox.id);
    flows.value = data?.payload || [];
  } catch {
    flows.value = [];
  } finally {
    isLoading.value = false;
  }
};

const toggle = () => {
  if (!props.canReply) return;
  isOpen.value = !isOpen.value;
  selected.value = null;
  if (isOpen.value) loadFlows();
};

const pick = flow => {
  if (flowTemplateUnavailableReason(flow.status)) return;
  selected.value = flow;
  cta.value = flow.name.slice(0, 30);
  body.value = t('KAPTION_WHATSAPP_FLOWS.COMPOSER.BODY_DEFAULT');
};

const send = async () => {
  if (!selected.value || !cta.value.trim() || !body.value.trim()) return;
  isSending.value = true;
  try {
    await KaptionWhatsappFlowsAPI.sendInConversation(props.conversationId, {
      flow_id: selected.value.id,
      cta: cta.value.trim(),
      body: body.value.trim(),
    });
    useAlert(t('KAPTION_WHATSAPP_FLOWS.COMPOSER.SENT'));
    isOpen.value = false;
    selected.value = null;
  } catch (error) {
    const code =
      error?.response?.data?.error_code || error?.response?.data?.error;
    useAlert(
      code === 'window_closed'
        ? t('KAPTION_WHATSAPP_FLOWS.COMPOSER.WINDOW_CLOSED')
        : t('KAPTION_WHATSAPP_FLOWS.COMPOSER.FAILED')
    );
  } finally {
    isSending.value = false;
  }
};
</script>

<template>
  <div v-if="isVisible" class="relative">
    <NextButton
      v-tooltip.top-start="tooltip"
      type="button"
      slate
      faded
      sm
      icon="i-lucide-list-checks"
      :disabled="!canReply"
      :aria-label="t('KAPTION_WHATSAPP_FLOWS.COMPOSER.BUTTON')"
      :title="tooltip"
      data-testid="kaption-flow-composer-action"
      @click="toggle"
    />
    <div
      v-if="isOpen"
      class="absolute z-50 flex flex-col gap-2 p-3 border shadow-lg bottom-10 start-0 w-80 rounded-xl border-n-weak bg-n-solid-2"
      data-testid="kaption-flow-composer-panel"
    >
      <div class="text-sm font-semibold text-n-slate-12">
        {{ t('KAPTION_WHATSAPP_FLOWS.COMPOSER.TITLE') }}
      </div>
      <div v-if="isLoading" class="flex justify-center py-4">
        <Spinner />
      </div>
      <template v-else-if="!selected">
        <p
          v-if="emptyMessage"
          class="text-sm text-n-slate-11"
          data-testid="kaption-flow-composer-empty"
        >
          {{ emptyMessage }}
        </p>
        <div class="flex flex-col gap-0.5 overflow-y-auto max-h-64">
          <button
            v-for="option in flowOptions"
            :key="option.flow.id"
            type="button"
            :disabled="option.disabled"
            :title="option.reason || undefined"
            class="flex items-center gap-2 px-2 py-1.5 text-sm text-left rounded-lg text-n-slate-12 enabled:hover:bg-n-alpha-black2 disabled:cursor-not-allowed disabled:opacity-60"
            data-testid="kaption-flow-composer-option"
            @click="pick(option.flow)"
          >
            <span
              class="flex-shrink-0 i-lucide-clipboard-list size-4 text-n-slate-10"
            />
            <span class="min-w-0 truncate">{{ option.flow.name }}</span>
            <span
              v-if="option.reason"
              class="flex-shrink-0 text-xs ms-auto text-n-slate-10"
              data-testid="kaption-flow-composer-reason"
            >
              {{ option.reason }}
            </span>
          </button>
        </div>
      </template>
      <template v-else>
        <div class="text-xs text-n-slate-11">{{ selected.name }}</div>
        <label class="flex flex-col gap-1 text-xs text-n-slate-11">
          {{ t('KAPTION_WHATSAPP_FLOWS.COMPOSER.BODY') }}
          <textarea
            v-model="body"
            rows="2"
            maxlength="1024"
            class="px-2 py-1 mb-0 text-sm rounded-lg bg-n-alpha-black2 text-n-slate-12"
          />
        </label>
        <label class="flex flex-col gap-1 text-xs text-n-slate-11">
          {{ t('KAPTION_WHATSAPP_FLOWS.COMPOSER.CTA') }}
          <input
            v-model="cta"
            maxlength="30"
            class="h-8 px-2 mb-0 text-sm rounded-lg bg-n-alpha-black2 text-n-slate-12"
            data-testid="kaption-flow-composer-cta"
          />
        </label>
        <div class="flex justify-between">
          <NextButton
            xs
            slate
            link
            :label="t('KAPTION_WHATSAPP_FLOWS.COMPOSER.BACK')"
            @click="selected = null"
          />
          <NextButton
            xs
            :is-loading="isSending"
            :disabled="!cta.trim() || !body.trim()"
            :label="t('KAPTION_WHATSAPP_FLOWS.COMPOSER.SEND')"
            data-testid="kaption-flow-composer-send"
            @click="send"
          />
        </div>
      </template>
    </div>
  </div>
  <template v-else />
</template>
